/**
 * Shader prewarm — compile every lazily-created VFX program during boot.
 *
 * The ability/telegraph/particle materials are built on FIRST USE, and on the
 * WebGL2 fallback (the deployed box has no TLS, so no WebGPU) each new node
 * graph is a synchronous GLSL compile: measured 23-36ms on the first cast of
 * each ability, 77.8ms worst when several landed together mid-fight. This module
 * fires one instance of each unique material graph at a point far below the
 * arena, then asks the renderer to compile everything it can see —
 * `compileAsync` rides `KHR_parallel_shader_compile` on the WebGL backend, so
 * the work happens off the critical path while the boot overlay / countdown owns
 * the screen. The transients expire through the normal per-frame updates.
 *
 * What each call below covers (programs, not instances):
 *   - spawnParticles ×4   → the pooled particle programs: soft circle and cel
 *                           shard, both blend states, seeding the pools too
 *   - prewarmAbilityVFX   → every CelShapes graph (slash arc, burst ring, star,
 *                           hex ring/shell, line wall, zone ring, bolt) plus
 *                           the pillar/dome graphs — fired immediately, no
 *                           beat scheduling, so compileAsync sees them all
 *   - the telegraph loop  → decalMaterial bakes the character color, so every
 *                           (shape, color) pair the roster can produce is
 *                           compiled: cone/circle/line × five characters
 */

import * as THREE from 'three/webgpu';
import { CHARACTERS } from '@chaos/protocol';
import { spawnParticles } from './ScreenEffects.js';
import { prewarmAbilityVFX } from '../combat/AbilityVFX.js';
import { showTelegraph } from '../combat/Telegraphs.js';

/** Far enough below any floor that nothing is visible even without occlusion. */
const HIDE_Y = -700;

let _done = false;

export async function prewarmShaders(renderer, scene, camera) {
  if (_done) return;
  _done = true;

  const origin = new THREE.Vector3(0, HIDE_Y, 0);

  try {
    spawnParticles(origin, 0xffffff, 4, 2, { lifetime: 400 });
    spawnParticles(origin, 0xffffff, 4, 2, { lifetime: 400, blending: 'additive' });
    spawnParticles(origin, 0xffffff, 4, 2, { lifetime: 400, shape: 'shard' });
    spawnParticles(origin, 0xffffff, 4, 2, { lifetime: 400, shape: 'shard', blending: 'additive' });

    prewarmAbilityVFX({ x: origin.x, y: origin.y, z: origin.z });

    for (const [characterId, def] of Object.entries(CHARACTERS)) {
      for (const slot of ['basic', 'skill1', 'skill2', 'ultimate']) {
        showTelegraph({
          abilityId: def.abilities[slot].id,
          characterId,
          x: 0,
          y: HIDE_Y,
          z: 0,
          yaw: 0,
        });
      }
    }

    await renderer.compileAsync?.(scene, camera);
  } catch (err) {
    // Prewarm is an optimisation; a failure must never block the boot.
    console.warn('[prewarm]', err);
  }
}
